// A transition SURFACE is an ordered GROUP of routes. That single idea covers both things a surface has to do,
// and keeps them consistent with each other:
//
//   • a SWIPE steps one place (`swipeTargets`) — a finger drags one page in from the adjacent edge.
//   • a LINK to ANY member of the group slides straight there in ONE screen (`findLinkTransition`) — clicking
//     Report → Board animates as a single slide, not a flip through the tabs between them, and not an instant
//     swap with no animation because the two aren't neighbours.
//
// The DIRECTION of both falls out of the order: a member further down the list arrives from the right (a
// push), one further up leaves to the right (a pop). So the array you render the tab bar from is the same
// array that decides which way everything travels — they cannot drift apart.
//
// This file is the SHARED, app-agnostic contract of the transition system — pure helpers, the outlet props,
// and the tuning constants. The actual surface GROUPS (which routes, in which order, for whom) are
// app-specific and live OUTSIDE this file: static apps declare them as constants (see a `surfaces.ts`),
// parameterised apps build the concrete member paths at each mount site. That split is what lets this file
// stay byte-identical across the projects that share this engine, so an edit ports verbatim.
//
// `RoutePath` is a plain `string`: a group's members are the CONCRETE pathnames the router navigates to
// (`/teams/42/report`, `/admin/doctors`) — exactly what `router.navigate({ to })` and the live preview's
// memory history consume. Apps that want compile-time route-literal safety keep it where they DEFINE their
// groups (via `satisfies readonly FileRouteTypes['to'][]`), not here.

/** A concrete, navigable pathname (e.g. `/teams/42/report`). Runtime instances, not route identities. */
export type RoutePath = string

/** Which screen edge the incoming route travels from — this fixes both the direction and the z-order. */
export type TransitionEdge = 'left' | 'right'

export interface RouteTransition {
  /** The group member being left. */
  from: RoutePath
  /** The group member being committed to. */
  to: RoutePath
  /**
   * `'right'` — the target slides IN from the right and rides ON TOP (a "forward" push; drag leftwards).
   * `'left'`  — the current view slides OFF to the right on top, revealing the target (a "back" pop).
   */
  edge: TransitionEdge
}

/**
 * The props every transition outlet takes — the ONE public contract, so "drop-in replacement, same props" is
 * compiler-enforced rather than a claim in each file's header.
 */
export interface TransitionOutletProps {
  /** The ORDERED group of routes this surface owns. A swipe steps one member; a link to ANY member slides
   *  straight there in one screen, in the direction their order implies (see findLinkTransition). */
  group: readonly RoutePath[]
  /**
   * The path of the route this outlet is mounted IN — required for a nested surface, omitted for the root
   * one. It travels to the live preview's shadow router so a layout hosting the surface renders only its
   * <Outlet/> into the pane (see route-preview.tsx), and it scopes the snapshot cache and the debug dump.
   */
  previewFrom?: RoutePath
  /** Start the gesture only from a screen-edge strip (iOS back-swipe style) instead of anywhere. A native
   *  scroll can't be limited to an edge strip, so the scroll engine ignores it — accepted for contract parity. */
  edgeOnly?: boolean
  /**
   * The group's members are SIBLINGS (a tab strip), not a navigation hierarchy: suppress the stacked-page
   * depth cues — no seam shadow, no parallax — and make both panes travel at full speed, side by side, like
   * a flat pager. Omit for a hierarchical surface (the top-level section push/pop), where the pane
   * "underneath" trails and the leading edge casts a shadow.
   */
  flat?: boolean
  /** Classes for the STAGE — the box the panes slide inside. This is the travelled area, so it should span
   *  the full width the routes occupy; a nested surface usually has to break out of its parent's padding. */
  className?: string
  /**
   * Classes for each PANE — the live route AND every preview alike, so all panes carry them. This is where a
   * route's padding belongs: padding left on a container OUTSIDE the stage doesn't travel with the page.
   */
  contentClassName?: string
}

/** True when `pathname` is on `member` (exact or nested, e.g. `/teams/42/report/x` is on `/teams/42/report`). */
export function matchesFrom(pathname: string, member: string): boolean {
  return pathname === member || pathname.startsWith(member.endsWith('/') ? member : `${member}/`)
}

/**
 * Where `pathname` sits in the group, or -1. Matched by prefix, LONGEST first: a URL like `/teams/42/board`
 * must resolve to the Board member even if a shorter member is also a prefix. Exported for the scroll engine's
 * roster (it mounts the WHOLE group as slots, anchored on this index).
 */
export function indexOfMember(order: readonly RoutePath[], pathname: string): number {
  let best = -1
  let bestLength = -1
  for (let i = 0; i < order.length; i++) {
    const member = order[i]
    if (!member || !matchesFrom(pathname, member)) continue
    if (member.length > bestLength) {
      best = i
      bestLength = member.length
    }
  }
  return best
}

/** The one-step moves a SWIPE can make from `pathname` (0–2: one per edge). */
export function swipeTargets(order: readonly RoutePath[], pathname: string): RouteTransition[] {
  const i = indexOfMember(order, pathname)
  const here = order[i]
  if (i < 0 || !here) return []
  const out: RouteTransition[] = []
  const next = order[i + 1]
  const previous = order[i - 1]
  if (next) out.push({ from: here, to: next, edge: 'right' }) // drag left → push the next one in
  if (previous) out.push({ from: here, to: previous, edge: 'left' }) // drag right → pop back to the previous
  return out
}

/**
 * The slide to play for a link/back-forward from `from` to `to` — for ANY two distinct members, adjacent or
 * not. The direction comes from their order, so a jump across the group is one slide the correct way, and a
 * navigation that leaves the group (or stays put) returns undefined and is left alone.
 */
export function findLinkTransition(order: readonly RoutePath[], from: string, to: string): RouteTransition | undefined {
  const i = indexOfMember(order, from)
  const j = indexOfMember(order, to)
  const source = order[i]
  const target = order[j]
  if (i < 0 || j < 0 || i === j || !source || !target) return undefined
  return { from: source, to: target, edge: j > i ? 'right' : 'left' }
}

/**
 * The path a group's nav chrome (tab bar) should highlight as active DURING a slide. The transition stores a
 * single global `override` (navVisualPath) that flips at the animation midpoint — but that override belongs to
 * whichever surface is sliding, and surfaces NEST. Honouring an override that targets no member of THIS group
 * dims every tab. So take the override only when it lands on one of `order`'s members; otherwise rest on the
 * committed `realPath`, keeping the tab you're leaving lit until the strip slides away.
 */
export function groupVisualPath(order: readonly string[], override: string | null, realPath: string): string {
  if (override !== null && order.some((member) => matchesFrom(override, member))) return override
  return realPath
}

/** Where the sliding underline should sit this frame: travelling between two members of its own group, or
 *  resting at one member. */
export type MarkerPlacement =
  | { mode: 'travel'; from: string; to: string; progress: number }
  | { mode: 'rest'; key: string }

/**
 * Decide the sliding underline's target for the current frame. It travels only when the running slide's BOTH
 * endpoints are members of this strip (`inGroup`); a slide belonging to another surface has nothing here to
 * travel between, so the marker rests under the committed `activePath` and rides away with it. No slide → rest.
 */
export function markerPlacement(
  slide: { from: string; to: string; progress: number } | null,
  inGroup: (member: string) => boolean,
  activePath: string,
): MarkerPlacement {
  if (slide && inGroup(slide.from) && inGroup(slide.to)) {
    const progress = slide.progress < 0 ? 0 : slide.progress > 1 ? 1 : slide.progress
    return { mode: 'travel', from: slide.from, to: slide.to, progress }
  }
  return { mode: 'rest', key: activePath }
}

export const TRANSITION_CONFIG = {
  /** Fraction of the surface width past which a release commits (below it, springs back). */
  commitThreshold: 0.4,
  /** Width (px) of the edge strip that starts a gesture on an `edgeOnly` surface. */
  edgeWidth: 28,
  /** Movement (px) before we lock the gesture to horizontal (and reject it as a vertical scroll). */
  intentSlop: 6,
  /** How far the underneath layer trails the top layer, as a fraction of its own travel (depth cue). */
  parallax: 0.28,
  /** Release speed (px/ms) in the commit direction that commits regardless of how far the drag got. */
  flickVelocity: 0.5,
  /** Spring driving the release/glide settle. Stiff and slightly UNDER-damped (ζ≈0.85) so it arrives fast
   *  with no soft tail. Shared with the link-glide sampling in spring.ts. */
  spring: { stiffness: 260, damping: 27, mass: 1 },
  /** The soft shadow the top pane casts along its leading edge. `peakAlpha` is a PEAK, not a constant. */
  shadow: { peakAlpha: 0.14, width: 36 },
} as const
